import React, { useEffect, useRef } from "react";
import { Text, TouchableOpacity, View, Modal } from "react-native";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { TEXT_STYLES } from "./BarcodeScannerStyles";

/** Same coverage intent as native `BARCODE_TYPES` in BarcodeScannerNative — explicit formats help 1D (EAN-13, UPC) on mobile web. */
function createWebZxingHints() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.ITF,
    BarcodeFormat.CODABAR,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.PDF_417,
    BarcodeFormat.AZTEC,
  ]);
  /* Extra decode passes (partially covered bars, skew, soft focus). */
  hints.set(DecodeHintType.TRY_HARDER, true);
  return hints;
}

const WEB_VIDEO_CONSTRAINTS = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1920, min: 640 },
    height: { ideal: 1080, min: 480 },
  },
  audio: false,
};

/**
 * After ZXing attaches the MediaStream, request continuous AF / AE where the browser supports it
 * (Chrome on many Android devices). Initial getUserMedia often ignores `focusMode` in `advanced`.
 */
async function applyScannerAutofocusHints(controls) {
  const apply = controls?.streamVideoConstraintsApply;
  if (typeof apply !== "function") return;
  const candidates = [
    { advanced: [{ focusMode: "continuous" }, { exposureMode: "continuous" }] },
    { advanced: [{ focusMode: "continuous" }] },
    { focusMode: "continuous" },
    { advanced: [{ exposureMode: "continuous" }] },
  ];
  for (const constraints of candidates) {
    try {
      await apply(constraints);
      return;
    } catch (_) {
      /* unsupported combination — try next */
    }
  }
}

function pointerClientCoords(e) {
  const ne = e?.nativeEvent ?? e;
  if (typeof e?.clientX === "number" && typeof e?.clientY === "number") {
    return { clientX: e.clientX, clientY: e.clientY };
  }
  if (ne?.changedTouches?.length) {
    const t = ne.changedTouches[0];
    return { clientX: t.clientX, clientY: t.clientY };
  }
  if (ne?.touches?.length) {
    const t = ne.touches[0];
    return { clientX: t.clientX, clientY: t.clientY };
  }
  if (typeof ne?.clientX === "number" && typeof ne?.clientY === "number") {
    return { clientX: ne.clientX, clientY: ne.clientY };
  }
  return null;
}

/** Tap-to-focus (normalized POI) — mainly Android Chrome; harmless no-ops elsewhere. */
async function tapToFocusFromPointer(controls, clientX, clientY, videoEl) {
  const apply = controls?.streamVideoConstraintsApply;
  if (typeof apply !== "function" || !videoEl?.getBoundingClientRect) return;
  const rect = videoEl.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;
  const poi = {
    x: Math.max(0.02, Math.min(0.98, x)),
    y: Math.max(0.02, Math.min(0.98, y)),
  };
  try {
    await apply({ advanced: [{ pointsOfInterest: [poi] }] });
    return;
  } catch (_) {
    /* fall through */
  }
  try {
    await apply({ advanced: [{ focusMode: "single-shot" }] });
  } catch (_) {
    /* ignore */
  }
}

/**
 * Expo CameraView on web often shows preview but does not fire onBarcodeScanned for 1D symbologies on iOS Safari.
 * ZXing reads frames from a plain HTML video stream and decodes reliably on mobile web.
 */
const BarcodeScannerWeb = ({ closeScanner, onBarCodeScanned, unmounCamera }) => {
  const videoRef = useRef(null);
  const locked = useRef(false);
  const scanControlsRef = useRef(null);
  const lastFocusTapRef = useRef(0);

  useEffect(() => {
    locked.current = false;
    scanControlsRef.current = null;
    let settleTimer = null;
    const reader = new BrowserMultiFormatReader(createWebZxingHints(), {
      delayBetweenScanAttempts: 120,
      delayBetweenScanSuccess: 350,
      tryPlayVideoTimeout: 8000,
    });
    let cancelled = false;
    let scannerControls = null;

    const handler = (result, _error, controls) => {
      if (cancelled || locked.current) return;
      if (!result) return;
      const text = result.getText();
      if (!text) return;
      locked.current = true;
      try {
        controls?.stop();
      } catch (_) {
        /* ignore */
      }
      try {
        scannerControls?.stop();
      } catch (_) {
        /* ignore */
      }
      onBarCodeScanned(text);
      unmounCamera();
    };

    const start = async () => {
      await new Promise((r) => setTimeout(r, 200));
      if (cancelled) return;
      const video = videoRef.current;
      if (!video) return;
      try {
        scannerControls = await reader.decodeFromConstraints(WEB_VIDEO_CONSTRAINTS, video, handler);
      } catch (e1) {
        if (cancelled) return;
        try {
          scannerControls = await reader.decodeFromConstraints(
            { video: { facingMode: { ideal: "environment" } }, audio: false },
            video,
            handler
          );
        } catch (e2) {
          if (cancelled) return;
          try {
            scannerControls = await reader.decodeFromVideoDevice(undefined, video, handler);
          } catch (e3) {
            console.warn("BarcodeScannerWeb:", e1, e2, e3);
          }
        }
      }
      if (cancelled || !scannerControls) return;
      scanControlsRef.current = scannerControls;
      await applyScannerAutofocusHints(scannerControls);
      /* Second pass after a short settle — some devices apply AF only after first frames. */
      settleTimer = setTimeout(() => {
        if (!cancelled && scanControlsRef.current === scannerControls) {
          applyScannerAutofocusHints(scannerControls).catch(() => {});
        }
      }, 600);
    };

    start();

    return () => {
      cancelled = true;
      locked.current = true;
      scanControlsRef.current = null;
      if (settleTimer) clearTimeout(settleTimer);
      try {
        scannerControls?.stop();
      } catch (_) {
        /* ignore */
      }
    };
  }, [onBarCodeScanned, unmounCamera]);

  const onPreviewPointer = (e) => {
    if (locked.current) return;
    const now = Date.now();
    if (now - lastFocusTapRef.current < 450) return;
    lastFocusTapRef.current = now;
    const coords = pointerClientCoords(e);
    if (!coords) return;
    const controls = scanControlsRef.current;
    const video = videoRef.current;
    if (controls && video) {
      tapToFocusFromPointer(controls, coords.clientX, coords.clientY, video).catch(() => {});
    }
  };

  const onCancel = () => {
    locked.current = true;
    closeScanner();
    unmounCamera();
  };

  const videoEl =
    typeof document !== "undefined"
      ? React.createElement("video", {
          ref: videoRef,
          style: {
            width: "100%",
            height: "100%",
            /* "contain" keeps full camera FOV visible so edges of a barcode are less likely cropped vs cover. */
            objectFit: "contain",
            display: "block",
            backgroundColor: "#000",
            touchAction: "manipulation",
          },
          autoPlay: true,
          muted: true,
          playsInline: true,
          onClick: onPreviewPointer,
          onTouchEnd: onPreviewPointer,
        })
      : null;

  return (
    <Modal transparent visible animationType="slide">
      <View style={{ flex: 1, position: "relative", backgroundColor: "#000" }}>
        <View style={TEXT_STYLES.cameraLeftOptionsContainer}>
          <TouchableOpacity onPress={onCancel} style={TEXT_STYLES.switchCameraButton}>
            <Text style={TEXT_STYLES.middle}>CANCEL</Text>
          </TouchableOpacity>
        </View>
        <View style={TEXT_STYLES.camera}>{videoEl}</View>
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 24,
            alignItems: "center",
            paddingHorizontal: 16,
          }}
        >
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, textAlign: "center" }}>
            Hold steady at ~15–25 cm · tap the preview to refocus
          </Text>
        </View>
      </View>
    </Modal>
  );
};

export default BarcodeScannerWeb;
